///Users/igorgalkin/Desktop/collaboration_platform/frontend/src/pages/QuestionnaireAnalyticsPage.js

import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import { alpha, useTheme } from '@mui/material/styles';

import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import TableViewOutlinedIcon from '@mui/icons-material/TableViewOutlined';

import { authAPI } from '../services/api';
import questionnaireAnalyticsAPI from '../services/questionnaireAnalyticsAPI';

const QuestionnaireAnalyticsPage = () => {
  const theme = useTheme();

  const [checkingAccess, setCheckingAccess] = useState(true);
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [downloading, setDownloading] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let active = true;

    authAPI
      .getProfile()
      .then((profile) => {
        if (active) {
          setIsSuperuser(profile?.is_superuser === true);
        }
      })
      .catch(() => {
        if (active) {
          setError(
            'Не удалось проверить права доступа. Обновите страницу.'
          );
        }
      })
      .finally(() => {
        if (active) {
          setCheckingAccess(false);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  const handleDownload = async (format) => {
    setError('');
    setSuccess('');
    setDownloading(format);

    try {
      if (format === 'csv') {
        await questionnaireAnalyticsAPI.exportCSV();
      } else {
        await questionnaireAnalyticsAPI.exportXLSX();
      }

      setSuccess(
        format === 'csv'
          ? 'Экспорт CSV подготовлен.'
          : 'Файл Excel подготовлен.'
      );
    } catch (downloadError) {
      if (downloadError.status === 403) {
        setError(
          'Доступ запрещён. Экспорт доступен только суперпользователю Django.'
        );
      } else {
        setError(
          downloadError.message || 'Не удалось сформировать файл.'
        );
      }
    } finally {
      setDownloading('');
    }
  };

  if (checkingAccess) {
    return (
      <Box
        sx={{
          minHeight: '50vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isSuperuser) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 4 },
          borderRadius: 4,
          border: `1px solid ${alpha(
            theme.palette.primary.main,
            0.14
          )}`,
          background: `linear-gradient(
            145deg,
            ${alpha(theme.palette.primary.main, 0.07)},
            rgba(255,255,255,0.96) 60%
          )`,
          boxShadow: '0 12px 36px rgba(29, 66, 72, 0.08)',
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ mb: 1.5 }}
        >
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: 3,
              display: 'grid',
              placeItems: 'center',
              color: 'primary.main',
              bgcolor: alpha(theme.palette.primary.main, 0.12),
            }}
          >
            <AssessmentOutlinedIcon fontSize="large" />
          </Box>

          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.5rem', sm: '2rem' },
              }}
            >
              Аналитика анкет
            </Typography>

            <Typography color="text.secondary" variant="body2">
              Экспорт ответов анкеты обратной связи
            </Typography>
          </Box>
        </Stack>

        <Typography
          color="text.secondary"
          sx={{ mt: 2, mb: 3, lineHeight: 1.7 }}
        >
          Скачайте ответы респондентов для дальнейшего анализа.
          CSV подходит для обработки в табличных редакторах и
          статистических инструментах, XLSX — для работы в Microsoft
          Excel.
        </Typography>

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setError('')}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess('')}
          >
            {success}
          </Alert>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={
              downloading === 'csv' ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <DownloadOutlinedIcon />
              )
            }
            disabled={Boolean(downloading)}
            onClick={() => handleDownload('csv')}
            sx={{
              py: 1.5,
              borderRadius: 2.5,
              fontWeight: 700,
            }}
          >
            {downloading === 'csv'
              ? 'Формируем CSV…'
              : 'Скачать CSV'}
          </Button>

          <Button
            fullWidth
            variant="outlined"
            size="large"
            startIcon={
              downloading === 'xlsx' ? (
                <CircularProgress size={18} />
              ) : (
                <TableViewOutlinedIcon />
              )
            }
            disabled={Boolean(downloading)}
            onClick={() => handleDownload('xlsx')}
            sx={{
              py: 1.5,
              borderRadius: 2.5,
              fontWeight: 700,
              borderWidth: 1.5,
            }}
          >
            {downloading === 'xlsx'
              ? 'Формируем Excel…'
              : 'Скачать Excel (.xlsx)'}
          </Button>
        </Stack>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 3 }}
        >
          Доступ к экспорту ограничен суперпользователями Django.
          Права также проверяются сервером.
        </Typography>
      </Paper>
    </Container>
  );
};

export default QuestionnaireAnalyticsPage;